import { z } from "zod";
import { QueryIRSchema } from "./queryBuilder";
import {
  CanvasComponentBaseSchema,
  ReportCanvasLayoutSchema,
} from "./reportCanvas";

/**
 * Serialized representation of a saved query-builder report.
 *
 * A saved report is two halves that only make sense together:
 *  - `query`  — the {@link QueryIRSchema} recipe (what data to fetch). Referenced
 *               by catalog field `key` only; the backend compiles it to SQL.
 *  - `report` — the visual layout built on top of the query's *result columns*
 *               (how to chart it). Chart field references (`xAxisKey`, …) point
 *               at result-column keys produced by `query`.
 *
 * The whole {@link ProcSavedReportDocument} is persisted as a single JSONB blob
 * (the `report` column of `core_saved_reports`). `schemaVersion` lets us migrate
 * old blobs as this shape evolves.
 *
 * This module is the single source of truth for the builtin chart component
 * shapes: the generic report canvas (`frontend/src/components/report-canvas/`)
 * imports these types rather than mirroring them. Each chart schema extends
 * {@link CanvasComponentBaseSchema}, so a component validated here also
 * satisfies the canvas's open persisted-component schema (`./reportCanvas.ts`).
 */

// ─── Version ─────────────────────────────────────────────────────────────────

/** Bump on any non-additive change to {@link ProcSavedReportDocumentSchema}. */
export const CURRENT_PROC_SAVED_REPORT_SCHEMA_VERSION = 1;

// ─── Primitives ──────────────────────────────────────────────────────────────

/** A hex color, e.g. "#8b5cf6" or "#abc". */
const HexColorSchema = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Must be a hex color");

/**
 * A chart axis / series reference: a *result-column key* (a select field key, or
 * an aggregate / date-bucket / relation-column alias). `null` = not yet chosen.
 * Existence is resolved against live result columns at load time, not here — the
 * query can be edited independently of the report.
 */
const ColumnRefSchema = z.string().nullable();

// ─── Chart components ────────────────────────────────────────────────────────

export const BarChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("bar-chart"),
  xAxisKey: ColumnRefSchema.default(null),
  yAxisKey: ColumnRefSchema.default(null),
  showGrid: z.boolean().default(true),
  showLabels: z.boolean().default(false),
});
export type BarChartComponent = z.infer<typeof BarChartComponentSchema>;

export const LineChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("line-chart"),
  xAxisKey: ColumnRefSchema.default(null),
  yAxisKey: ColumnRefSchema.default(null),
  filled: z.boolean().default(false),
  curve: z.enum(["linear", "monotone", "step"]).default("monotone"),
  showDots: z.boolean().default(true),
  showGrid: z.boolean().default(true),
});
export type LineChartComponent = z.infer<typeof LineChartComponentSchema>;

export const ScatterChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("scatter-chart"),
  xAxisKey: ColumnRefSchema.default(null),
  yAxisKey: ColumnRefSchema.default(null),
  showGrid: z.boolean().default(true),
});
export type ScatterChartComponent = z.infer<typeof ScatterChartComponentSchema>;

export const PieChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("pie-chart"),
  nameKey: ColumnRefSchema.default(null),
  valueKey: ColumnRefSchema.default(null),
  donut: z.boolean().default(false),
  showLabels: z.boolean().default(false),
  /** 0 = unlimited; otherwise top-N by value, rest grouped into "Other". */
  maxSlices: z.number().int().min(0).default(8),
  /** Cycled across slices by index. */
  colors: z.array(HexColorSchema).default([]),
});
export type PieChartComponent = z.infer<typeof PieChartComponentSchema>;

export const RadarChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("radar-chart"),
  subjectKey: ColumnRefSchema.default(null),
  valueKey: ColumnRefSchema.default(null),
  gridType: z.enum(["polygon", "circle"]).default("polygon"),
  filled: z.boolean().default(true),
  /** 0 = unlimited; otherwise top-N by value, rest grouped into "Other". */
  maxSections: z.number().int().min(0).default(8),
  /** Stroke + fill color for the series. */
  color: HexColorSchema.default("#8b5cf6"),
});
export type RadarChartComponent = z.infer<typeof RadarChartComponentSchema>;

// ─── Saved-report document (the JSONB blob) ──────────────────────────────────
//
// The `report` half is the OPEN canvas layout ({@link ReportCanvasLayoutSchema}
// in `./reportCanvas.ts`), the same serialization every report-canvas surface
// persists — so a saved report can carry any registered widget (vega-chart,
// future types), not just the builtin charts above. The per-chart schemas here
// remain the source of truth for the builtin chart config shapes (re-exported
// by the canvas), but no longer gate the layout.

export const ProcSavedReportDocumentSchema = z.object({
  schemaVersion: z.number().int(),
  query: QueryIRSchema,
  report: ReportCanvasLayoutSchema,
});
export type ProcSavedReportDocument = z.infer<
  typeof ProcSavedReportDocumentSchema
>;
export type ProcSavedReportDocumentInput = z.input<
  typeof ProcSavedReportDocumentSchema
>;

// ─── Serialize / deserialize ─────────────────────────────────────────────────

/**
 * Serialize an in-memory query + report into the persisted document shape,
 * stamping the current schema version. The returned object is validated and
 * ready to store in the `report` JSONB column (Sequelize handles JSON encoding).
 */
export function serializeProcSavedReport(input: {
  query: z.input<typeof QueryIRSchema>;
  report: z.input<typeof ReportCanvasLayoutSchema>;
}): ProcSavedReportDocument {
  return ProcSavedReportDocumentSchema.parse({
    schemaVersion: CURRENT_PROC_SAVED_REPORT_SCHEMA_VERSION,
    query: input.query,
    report: input.report,
  });
}

/**
 * Deserialize a stored JSONB blob back into a typed document. Throws on an
 * invalid blob. This is the single seam for future migrations — branch on
 * `raw.schemaVersion` here before validating once the shape changes.
 */
export function parseProcSavedReport(raw: unknown): ProcSavedReportDocument {
  return ProcSavedReportDocumentSchema.parse(raw);
}

/** Non-throwing variant; returns a Zod SafeParseReturnType. */
export function safeParseProcSavedReport(raw: unknown) {
  return ProcSavedReportDocumentSchema.safeParse(raw);
}
